import { useState, useEffect, useMemo } from 'react';
import { standaloneBEOsAPI, clientsAPI, menusAPI, selectableItemsAPI, contractsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const INITIAL_FORM_DATA = {
  client_name: '', client_email: '', client_phone: '', secondary_client_name: '',
  event_name: '', event_type: 'offsite', event_date: '', event_time: '',
  guest_count: '', location: '', location_type: 'offsite', service_style: '', bar_type: '',
  room: '', custom_menu: '',
  custom_menu_breakfast: '', custom_menu_lunch: '',
  custom_menu_cocktail: '', custom_menu_dinner: '', custom_menu_dessert: '', custom_menu_late_night: '',
  custom_menu_bar: '', custom_menu_beverage: '', custom_menu_on_the_turn: '',
  custom_menu_breakfast_price: '', custom_menu_lunch_price: '',
  custom_menu_cocktail_price: '', custom_menu_dinner_price: '', custom_menu_dessert_price: '', custom_menu_late_night_price: '',
  custom_menu_bar_price: '', custom_menu_beverage_price: '', custom_menu_on_the_turn_price: '',
  custom_menu_breakfast_tiers: [], custom_menu_lunch_tiers: [],
  custom_menu_cocktail_tiers: [], custom_menu_dinner_tiers: [], custom_menu_dessert_tiers: [], custom_menu_late_night_tiers: [],
  custom_menu_bar_tiers: [], custom_menu_beverage_tiers: [], custom_menu_on_the_turn_tiers: [],
  notes: '', selected_menus: [],
  table_style: '', tablecloth_color: '', napkin_color: '',
  dj_table: false, gift_table: false, head_table: false, water_station: false,
  coffee_station: false, bar_setup: false, cake_table: false, registration_table: false, dessert_station: false,
  event_start_time: '', cocktail_hour_time: '', dinner_start_time: '', event_end_time: '',
  special_requests: '',
  cocktail_coach_package: '', custom_package_notes: '',
  signature_cocktail_1: '', signature_cocktail_2: ''
};

const EMPTY_PLATED = { starters: {}, mains: {}, desserts: {}, beverages: {} };

export const napkinColors = ['White', 'Ivory', 'Black', 'Navy', 'Burgundy', 'Gold', 'Silver', 'Sage'];
export const tableclothColors = ['White', 'Ivory', 'Black', 'Navy', 'Burgundy'];
export const tableStyles = ['Round 60"', 'Round 72"', 'Banquet 6ft', 'Banquet 8ft', 'Mixed'];

export const useStandaloneBEOForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [beos, setBeos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingBeo, setEditingBeo] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [menus, setMenus] = useState([]);
  const [platedItems, setPlatedItems] = useState([]);
  const [platedSelections, setPlatedSelections] = useState(EMPTY_PLATED);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  useEffect(() => {
    fetchBEOs();
    fetchClients();
    fetchMenus();
    fetchPlatedItems();
  }, []);

  const fetchBEOs = async () => {
    try {
      setLoading(true);
      const data = await standaloneBEOsAPI.getAll();
      setBeos(data);
    } catch (error) {
      toast({ title: 'Error loading BEOs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try { setClients(await clientsAPI.getAll()); }
    catch (error) { console.error('Error fetching clients:', error); }
  };

  const fetchMenus = async () => {
    try { setMenus(await menusAPI.getAll()); }
    catch (error) { console.error('Error fetching menus:', error); }
  };

  const fetchPlatedItems = async () => {
    try { setPlatedItems(await selectableItemsAPI.getPlated()); }
    catch (error) { console.error('Error fetching plated items:', error); }
  };

  // Grouped plated items
  const groupedPlatedItems = useMemo(() => {
    return platedItems.reduce((acc, item) => {
      const category = item.sub_category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});
  }, [platedItems]);

  const starterItems = useMemo(() => [...(groupedPlatedItems.salad || []), ...(groupedPlatedItems.appetizer || [])], [groupedPlatedItems]);
  const mainItems = useMemo(() => groupedPlatedItems.main || [], [groupedPlatedItems]);
  const dessertItems = useMemo(() => groupedPlatedItems.dessert || [], [groupedPlatedItems]);
  const beverageItems = useMemo(() => groupedPlatedItems.beverage || [], [groupedPlatedItems]);

  const handlePlatedQuantityChange = (category, itemId, itemName, quantity, description = '') => {
    setPlatedSelections(prev => {
      const newSelections = { ...prev };
      if (quantity > 0) {
        newSelections[category] = { ...newSelections[category], [itemId]: { name: itemName, quantity, description } };
      } else {
        const { [itemId]: removed, ...rest } = newSelections[category];
        newSelections[category] = rest;
      }
      return newSelections;
    });
  };

  const getCategoryTotal = (category) => {
    return Object.values(platedSelections[category] || {}).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  const handleOpenDialog = (beo = null) => {
    if (beo) {
      setEditingBeo(beo);
      setFormData({
        client_name: beo.client_name || '', client_email: beo.client_email || '',
        client_phone: beo.client_phone || '', secondary_client_name: beo.secondary_client_name || '',
        event_name: beo.event_name || '', event_type: beo.event_type || 'offsite',
        event_date: beo.event_date || '', event_time: beo.event_time || '',
        guest_count: beo.guest_count || '', location: beo.location || '',
        location_type: beo.location_type || 'offsite', service_style: beo.service_style || '',
        bar_type: beo.bar_type || '', room: beo.room || '', custom_menu: beo.custom_menu || '',
        custom_menu_breakfast: beo.custom_menu_breakfast || '',
        custom_menu_lunch: beo.custom_menu_lunch || '',
        custom_menu_cocktail: beo.custom_menu_cocktail || '',
        custom_menu_dinner: beo.custom_menu_dinner || '',
        custom_menu_dessert: beo.custom_menu_dessert || '',
        custom_menu_late_night: beo.custom_menu_late_night || '',
        custom_menu_bar: beo.custom_menu_bar || '',
        custom_menu_beverage: beo.custom_menu_beverage || '',
        custom_menu_on_the_turn: beo.custom_menu_on_the_turn || '',
        custom_menu_breakfast_price: beo.custom_menu_breakfast_price ?? '',
        custom_menu_lunch_price: beo.custom_menu_lunch_price ?? '',
        custom_menu_cocktail_price: beo.custom_menu_cocktail_price ?? '',
        custom_menu_dinner_price: beo.custom_menu_dinner_price ?? '',
        custom_menu_dessert_price: beo.custom_menu_dessert_price ?? '',
        custom_menu_late_night_price: beo.custom_menu_late_night_price ?? '',
        custom_menu_bar_price: beo.custom_menu_bar_price ?? '',
        custom_menu_beverage_price: beo.custom_menu_beverage_price ?? '',
        custom_menu_on_the_turn_price: beo.custom_menu_on_the_turn_price ?? '',
        custom_menu_breakfast_tiers: beo.custom_menu_breakfast_tiers || [],
        custom_menu_lunch_tiers: beo.custom_menu_lunch_tiers || [],
        custom_menu_cocktail_tiers: beo.custom_menu_cocktail_tiers || [],
        custom_menu_dinner_tiers: beo.custom_menu_dinner_tiers || [],
        custom_menu_dessert_tiers: beo.custom_menu_dessert_tiers || [],
        custom_menu_late_night_tiers: beo.custom_menu_late_night_tiers || [],
        custom_menu_bar_tiers: beo.custom_menu_bar_tiers || [],
        custom_menu_beverage_tiers: beo.custom_menu_beverage_tiers || [],
        custom_menu_on_the_turn_tiers: beo.custom_menu_on_the_turn_tiers || [],
        notes: beo.notes || '',
        selected_menus: beo.selected_menus || [],
        table_style: beo.table_style || '', tablecloth_color: beo.tablecloth_color || '',
        napkin_color: beo.napkin_color || '',
        dj_table: beo.dj_table || false, gift_table: beo.gift_table || false,
        head_table: beo.head_table || false, water_station: beo.water_station || false,
        coffee_station: beo.coffee_station || false, bar_setup: beo.bar_setup || false,
        cake_table: beo.cake_table || false, registration_table: beo.registration_table || false,
        dessert_station: beo.dessert_station || false,
        event_start_time: beo.event_start_time || '', cocktail_hour_time: beo.cocktail_hour_time || '',
        dinner_start_time: beo.dinner_start_time || '', event_end_time: beo.event_end_time || '',
        special_requests: beo.special_requests || ''
      });
      setPlatedSelections({
        starters: beo.plated_starters || {}, mains: beo.plated_mains || {},
        desserts: beo.plated_desserts || {}, beverages: beo.plated_beverages || {}
      });
    } else {
      setEditingBeo(null);
      setFormData(INITIAL_FORM_DATA);
      setPlatedSelections(EMPTY_PLATED);
    }
    setShowDialog(true);
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData(prev => ({ ...prev, client_name: client.name, client_email: client.email || '', client_phone: client.phone || '' }));
    }
  };

  const handleSave = async () => {
    if (!formData.client_name || !formData.event_name || !formData.event_date || !formData.guest_count) {
      toast({ title: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const sanitizeTiersBEO = (tiers) => {
        if (!Array.isArray(tiers) || tiers.length === 0) return [];
        return tiers
          .filter(t => (t.label || '').toString().trim() || t.quantity || t.price_per_person)
          .map(t => ({
            label: (t.label || '').toString().trim() || null,
            quantity: t.quantity ? parseInt(t.quantity) : null,
            price_per_person: t.price_per_person ? parseFloat(t.price_per_person) : null,
          }));
      };
      const payload = {
        ...formData,
        guest_count: parseInt(formData.guest_count),
        custom_menu_breakfast_price: formData.custom_menu_breakfast_price === '' ? null : (parseFloat(formData.custom_menu_breakfast_price) || null),
        custom_menu_lunch_price: formData.custom_menu_lunch_price === '' ? null : (parseFloat(formData.custom_menu_lunch_price) || null),
        custom_menu_cocktail_price: formData.custom_menu_cocktail_price === '' ? null : (parseFloat(formData.custom_menu_cocktail_price) || null),
        custom_menu_dinner_price: formData.custom_menu_dinner_price === '' ? null : (parseFloat(formData.custom_menu_dinner_price) || null),
        custom_menu_dessert_price: formData.custom_menu_dessert_price === '' ? null : (parseFloat(formData.custom_menu_dessert_price) || null),
        custom_menu_late_night_price: formData.custom_menu_late_night_price === '' ? null : (parseFloat(formData.custom_menu_late_night_price) || null),
        custom_menu_bar_price: formData.custom_menu_bar_price === '' ? null : (parseFloat(formData.custom_menu_bar_price) || null),
        custom_menu_beverage_price: formData.custom_menu_beverage_price === '' ? null : (parseFloat(formData.custom_menu_beverage_price) || null),
        custom_menu_on_the_turn_price: formData.custom_menu_on_the_turn_price === '' ? null : (parseFloat(formData.custom_menu_on_the_turn_price) || null),
        custom_menu_breakfast_tiers: sanitizeTiersBEO(formData.custom_menu_breakfast_tiers),
        custom_menu_lunch_tiers: sanitizeTiersBEO(formData.custom_menu_lunch_tiers),
        custom_menu_cocktail_tiers: sanitizeTiersBEO(formData.custom_menu_cocktail_tiers),
        custom_menu_dinner_tiers: sanitizeTiersBEO(formData.custom_menu_dinner_tiers),
        custom_menu_dessert_tiers: sanitizeTiersBEO(formData.custom_menu_dessert_tiers),
        custom_menu_late_night_tiers: sanitizeTiersBEO(formData.custom_menu_late_night_tiers),
        custom_menu_bar_tiers: sanitizeTiersBEO(formData.custom_menu_bar_tiers),
        custom_menu_beverage_tiers: sanitizeTiersBEO(formData.custom_menu_beverage_tiers),
        custom_menu_on_the_turn_tiers: sanitizeTiersBEO(formData.custom_menu_on_the_turn_tiers),
        plated_starters: formData.service_style === 'plated' ? platedSelections.starters : {},
        plated_mains: formData.service_style === 'plated' ? platedSelections.mains : {},
        plated_desserts: formData.service_style === 'plated' ? platedSelections.desserts : {},
        plated_beverages: formData.service_style === 'plated' ? platedSelections.beverages : {}
      };
      if (editingBeo) {
        await standaloneBEOsAPI.update(editingBeo.id, payload);
        toast({ title: 'BEO updated successfully!' });
      } else {
        await standaloneBEOsAPI.create(payload);
        toast({ title: 'BEO created successfully!' });
      }
      setShowDialog(false);
      fetchBEOs();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error saving BEO', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (beo) => {
    if (!window.confirm(`Delete BEO "${beo.beo_number}"?`)) return;
    try {
      await standaloneBEOsAPI.delete(beo.id);
      toast({ title: 'BEO deleted successfully!' });
      fetchBEOs();
    } catch (error) {
      toast({ title: 'Error deleting BEO', variant: 'destructive' });
    }
  };

  const handleConvertToContract = async (beo) => {
    if (!window.confirm(`Convert ${beo.beo_number} to a contract? This will create a new contract with the BEO's details.`)) return;
    try {
      const result = await standaloneBEOsAPI.convertToContract(beo.id);
      toast({ title: `Contract ${result.contract_number} created!` });
      fetchBEOs();
      navigate('/contracts');
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error converting to contract', variant: 'destructive' });
    }
  };

  const handleOpenConvertedContract = async (beo) => {
    if (!beo.contract_id) return;
    try {
      const contract = await contractsAPI.getById(beo.contract_id);
      navigate('/contract-preview', { state: { contract } });
    } catch (error) {
      toast({ title: 'Could not open contract — it may have been deleted.', variant: 'destructive' });
    }
  };

  const filteredBeos = beos
    .filter(beo =>
      beo.event_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beo.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beo.beo_number?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const da = a.event_date || '';
      const db = b.event_date || '';
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  return {
    beos, loading, showDialog, setShowDialog, saving, editingBeo,
    clients, searchTerm, setSearchTerm, menus, platedItems,
    platedSelections, formData, setFormData,
    starterItems, mainItems, dessertItems, beverageItems,
    filteredBeos, navigate,
    handlePlatedQuantityChange, getCategoryTotal, handleOpenDialog,
    handleClientSelect, handleSave, handleDelete, handleConvertToContract,
    handleOpenConvertedContract,
    getStatusColor
  };
};
